/**
 * What a Shorts board's dashboard card says (F-140, docs/11 §4.3) — PURE,
 * so the rules are unit-tested rather than eyeballed across five columns.
 *
 * THE DATE CHIP FOLLOWS THE STAGE, the way the Script-due chip does on a
 * long-form card: the one date that is the question in this column. Red once
 * that day has passed with the step not done — the card is the reminder, so
 * the reminder has to be readable across a column without opening anything.
 *
 * THE STRIP is the slots' state, the one thing about a Shorts board its stage
 * cannot say: the stage ends at Editing, while a short goes on to be
 * released. A board holds ONE Short since F-143, so its strip is one dot and
 * the state's name; a week of five from before reads "2 of 5 cut", because
 * there the board is in Editing as a batch while Short 3 is already out.
 */
import type { BoardSummary, ShortSlot, ShortState } from "@kola/shared";

export interface ShortsCardChip {
  /** "Script due", "Shoot", "Edit due", "Release" ("Release 3" on a week of five). */
  label: string;
  /** `YYYY-MM-DD`. */
  dateKey: string;
  /** The day has passed and the step it names is not done. */
  overdue: boolean;
}

/** The slots in release order (the stored order is the release order, but never trust it). */
export function orderedSlots(board: Pick<BoardSummary, "shorts">): ShortSlot[] {
  return [...board.shorts].sort((a, b) => a.slot - b.slot);
}

/**
 * The chip for a Shorts card in its current stage, or null when there is
 * nothing left to wait for (every short released). `today` is a `YYYY-MM-DD`
 * key; keys compare lexically.
 */
export function shortsCardChip(
  board: Pick<BoardSummary, "status" | "scriptFinishDate" | "shootDate" | "editDueDate" | "shorts">,
  today: string,
): ShortsCardChip | null {
  const chip = (label: string, dateKey: string | null): ShortsCardChip | null =>
    dateKey === null ? null : { label, dateKey, overdue: dateKey < today };
  switch (board.status) {
    case "scripting":
      return chip(board.shorts.length > 1 ? "Scripts due" : "Script due", board.scriptFinishDate);
    case "storyboarding":
    case "request_review":
    case "shooting":
      return chip("Shoot", board.shootDate);
    case "editing": {
      // While the edit deadline is still ahead it is the question; once it
      // has passed, the next release is — the cut is late only until it ships.
      const editDue = board.editDueDate;
      const next = orderedSlots(board).find((slot) => slot.state !== "released");
      if (next === undefined) return null;
      if (editDue !== null && today <= editDue && next.state !== "cut") {
        return chip("Edit due", editDue);
      }
      return chip(board.shorts.length === 1 ? "Release" : `Release ${next.slot}`, next.releaseDate);
    }
    default:
      return null;
  }
}

/** How many slots have reached at least `state` — states are ordered. */
function countAtLeast(slots: readonly ShortSlot[], state: ShortState): number {
  const rank: Record<ShortState, number> = { planned: 0, shot: 1, cut: 2, released: 3 };
  return slots.filter((slot) => rank[slot.state] >= rank[state]).length;
}

const STATE_NAME: Record<ShortState, string> = {
  planned: "Planned",
  shot: "Shot",
  cut: "Cut",
  released: "Released",
};

/**
 * The strip's caption. A one-Short board (F-143) says where its short is —
 * "Planned", "Cut", "Released". A week of five from before says the furthest
 * state any short has reached, counted — "2 of 5 cut" — or "5 planned"
 * before the shoot. A board an older server sent with no slots counts as
 * one Short.
 */
export function shortsStripLabel(board: Pick<BoardSummary, "shorts">): string {
  const slots = board.shorts;
  if (slots.length <= 1) return STATE_NAME[slots[0]?.state ?? "planned"];
  const total = slots.length;
  const released = countAtLeast(slots, "released");
  if (released > 0) return `${released} of ${total} released`;
  const cut = countAtLeast(slots, "cut");
  if (cut > 0) return `${cut} of ${total} cut`;
  const shot = countAtLeast(slots, "shot");
  if (shot > 0) return `${shot} of ${total} shot`;
  return `${total} planned`;
}

/** First and last release day, in slot order — for "Releases Sep 28 → Oct 2". */
export function releaseSpan(board: Pick<BoardSummary, "shorts">): { first: string; last: string } | null {
  const slots = orderedSlots(board);
  const first = slots[0];
  const last = slots[slots.length - 1];
  if (first === undefined || last === undefined) return null;
  return { first: first.releaseDate, last: last.releaseDate };
}

/** The dashboard's kind filter (F-140): everything, or one kind. */
export type KindFilter = "all" | "long" | "shorts";

export function matchesKindFilter(board: Pick<BoardSummary, "kind">, filter: KindFilter): boolean {
  return filter === "all" || board.kind === filter;
}
